import { AccountGroup } from "@common/types/domains/accounting/account";
import {
    AccountMergeStatus,
    PreviewAccountChange,
} from "@common/types/domains/accounting/chartOfAccountsTemplateApply";
import { CoaTemplateApplyChangeSet } from "@common/types/domains/accounting/chartOfAccountsTemplateApplyEvent";
import type { ChartOfAccountsTemplateApplyEventDetail } from "@common/schemas/domains/accounting/chart-of-accounts-template-apply-event.schema";
import { CoaPostApplySnapshot } from "@common/schemas/domains/accounting/chart-of-accounts-template-post-apply-snapshot.schema";
import {
    buildHistoricalAccountRow,
    formatHistoricalGroupPath,
    type HistoricalCoaViewModel,
} from "./applyCoaTemplateHistoricalViewModel";

// The tag/state fields the sparse change set overlays onto a snapshot row. The
// snapshot stays authoritative for identity, code, type, placement and order;
// only these change-driven fields differ between a touched and untouched row.
interface AccountTagOverlay {
    changes: PreviewAccountChange[];
    mergeStatus: AccountMergeStatus;
    wasArchivedByApply: boolean;
    previousGroupPath: string | null;
}

function buildOverlayByAccountId(
    changeSet: CoaTemplateApplyChangeSet,
): Map<string, AccountTagOverlay> {
    const overlayById = new Map<string, AccountTagOverlay>();
    for (const account of changeSet.createdAccounts) {
        overlayById.set(account.accountId, {
            changes: [PreviewAccountChange.added],
            mergeStatus: AccountMergeStatus.new,
            wasArchivedByApply: false,
            previousGroupPath: null,
        });
    }
    for (const account of changeSet.updatedAccounts) {
        const changes: PreviewAccountChange[] = [];
        let previousGroupPath: string | null = null;
        const groupChange = account.changes.group;
        const isReassigned = groupChange !== undefined;
        if (groupChange) {
            changes.push(PreviewAccountChange.reassigned);
            previousGroupPath = formatHistoricalGroupPath(
                groupChange.before.groupPath,
            );
        }
        // A reassignment already implies the resulting position shift, so it
        // stays `updated` (never `moved`) — matching the live engine, where a
        // group sync is `updated` and `moved` is reserved for an ordering-only
        // change. This prevents a single reassignment badging as both
        // "Reassigned" and "Moved".
        const hasSortOrderChange = account.changes.sortOrder !== undefined;
        const disabledChange = account.changes.isDisabled;
        const wasArchivedByApply =
            disabledChange !== undefined &&
            !disabledChange.before &&
            disabledChange.after;
        if (wasArchivedByApply) {
            changes.push(PreviewAccountChange.archived);
        }
        overlayById.set(account.accountId, {
            changes,
            mergeStatus:
                hasSortOrderChange && !isReassigned
                    ? AccountMergeStatus.moved
                    : AccountMergeStatus.updated,
            wasArchivedByApply,
            previousGroupPath,
        });
    }
    return overlayById;
}

// Builds the historical view model from the frozen post-apply snapshot: it
// renders the FULL chart (every account + the pre-existing/created group
// hierarchy) using stable persisted ids, then overlays the sparse change set so
// only the touched rows carry Added/Reassigned/Moved/Archived tags. It never
// reconstructs data from the current template/ledger — the snapshot is
// immutable, so the tree is identical for Applied and Reverted events.
export function mapSnapshotToHistoricalCoaInputs({
    snapshot,
    changeSet,
}: {
    snapshot: CoaPostApplySnapshot;
    changeSet: CoaTemplateApplyChangeSet;
}): HistoricalCoaViewModel {
    const overlayById = buildOverlayByAccountId(changeSet);

    const accounts = snapshot.accounts.map((account) => {
        const overlay = overlayById.get(account.id);
        return buildHistoricalAccountRow({
            id: account.id,
            code: account.code,
            codeText: account.codeText,
            name: account.name,
            type: account.type,
            isDisabled: account.isDisabled,
            isManuallyCreated: account.isManuallyCreated,
            parentGroupId: account.parentGroupId,
            sortOrder: account.sortOrder,
            mergeStatus: overlay?.mergeStatus ?? AccountMergeStatus.unchanged,
            changes: overlay?.changes ?? [],
            wasArchivedByApply: overlay?.wasArchivedByApply ?? false,
            previousGroupPath: overlay?.previousGroupPath ?? null,
        });
    });

    const groups: AccountGroup[] = snapshot.groups.map((group) => ({
        id: group.id,
        entityId: group.entityId,
        name: group.name,
        type: group.type,
        parentGroupId: group.parentGroupId,
        sortOrder: group.sortOrder,
    }));

    const newGroupIds = new Set(
        changeSet.createdGroups.map((group) => group.groupId),
    );

    const isEmpty =
        accounts.length === 0 &&
        groups.length === 0 &&
        changeSet.variableMappingChanges.length === 0;

    return {
        accounts,
        groups,
        newGroupIds,
        variableMappingChanges: changeSet.variableMappingChanges,
        isEmpty,
    };
}

export function mapApplyEventToHistoricalCoaInputs(
    detail: Pick<
        ChartOfAccountsTemplateApplyEventDetail,
        "changeSet" | "postApplySnapshot"
    >,
): HistoricalCoaViewModel {
    return mapSnapshotToHistoricalCoaInputs({
        snapshot: detail.postApplySnapshot,
        changeSet: detail.changeSet,
    });
}
